import { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Card } from '@/components/Shared/Card';
import { Badge } from '@/components/Shared/Badge';
import {
  useNetzplanImportStore,
  type TaskDecision,
  type TaskSource,
} from '@/store/useNetzplanImportStore';
import type { ScheduleTyp } from '@/pages/Import/logic/mapping';
import { StepFooter } from './StepFooter';

const INPUT =
  'w-full px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50';
const ROW = 'grid grid-cols-[minmax(0,1.5fr)_minmax(0,1.3fr)_120px_150px_84px] gap-3 px-3 py-2 items-center';
const TYPS: ScheduleTyp[] = ['Wöchentlich', '2-wöchentlich', 'Monatlich', 'Saisonal'];

export function TaskStep() {
  const { t } = useTranslation('import');
  const { taskSources, taskDecisions, objectSources, objectDecisions, setTaskDecision, missingServiceNames } =
    useNetzplanImportStore();
  const [search, setSearch] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(false);

  const objName = useMemo(() => new Map(objectSources.map((o) => [o.key, o.objName])), [objectSources]);

  // Tasks only make sense when their object is not skipped.
  const activeSources = useMemo(
    () => taskSources.filter((s) => objectDecisions[s.objectKey]?.action !== 'skip'),
    [taskSources, objectDecisions],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return activeSources.filter((s) => {
      if (onlyMissing && !s.missingService) return false;
      if (q && !(objName.get(s.objectKey) ?? '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [activeSources, onlyMissing, search, objName]);

  const createCount = useMemo(
    () => activeSources.filter((s) => taskDecisions[s.id]?.action === 'create').length,
    [activeSources, taskDecisions],
  );
  const missing = missingServiceNames();

  return (
    <Card>
      <div className="space-y-4">
        <p className="text-[13px] text-slate-600">{t('tasks.hint')}</p>

        {missing.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-px" strokeWidth={2} />
            <span>{t('tasks.missingServices', { names: missing.join(', ') })}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <input
            className={`${INPUT} max-w-xs`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('tasks.searchObject')}
          />
          <label className="flex items-center gap-2 text-[12px] text-slate-600">
            <input
              type="checkbox"
              checked={onlyMissing}
              onChange={(e) => setOnlyMissing(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
            />
            {t('tasks.onlyMissing')}
          </label>
          <span className="text-[12px] text-slate-500 ml-auto tabular-nums">
            {t('tasks.shown', { shown: rows.length, total: activeSources.length })}
          </span>
        </div>

        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className={`${ROW} bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400`}>
            <span>{t('tasks.colObject')}</span>
            <span>{t('tasks.colService')}</span>
            <span>{t('tasks.colTyp')}</span>
            <span>{t('tasks.colWindow')}</span>
            <span>{t('tasks.colActive')}</span>
          </div>

          <div className="max-h-[52vh] overflow-y-auto divide-y divide-slate-100">
            {rows.slice(0, 400).map((src) => (
              <TaskRow
                key={src.id}
                src={src}
                object={objName.get(src.objectKey) ?? ''}
                decision={taskDecisions[src.id]}
                onChange={(patch) => setTaskDecision(src.id, patch)}
              />
            ))}
            {rows.length === 0 && (
              <p className="px-3 py-6 text-center text-[12px] text-slate-500">{t('tasks.empty')}</p>
            )}
            {rows.length > 400 && (
              <p className="px-3 py-3 text-center text-[11px] text-slate-400">{t('tasks.truncated', { count: rows.length - 400 })}</p>
            )}
          </div>
        </div>

        <StepFooter back="objects" next="commit" summary={t('tasks.summary', { create: createCount })} />
      </div>
    </Card>
  );
}

function TaskRow({
  src,
  object,
  decision,
  onChange,
}: {
  src: TaskSource;
  object: string;
  decision: TaskDecision | undefined;
  onChange: (patch: Partial<TaskDecision>) => void;
}) {
  const { t } = useTranslation('import');
  if (!decision) return null;
  const active = decision.action === 'create';

  return (
    <div className={`${ROW} ${active ? '' : 'opacity-55'}`}>
      <div className="min-w-0">
        <div className="text-[13px] text-slate-900 truncate">{object}</div>
        <div className="text-[11px] text-slate-500 truncate">{src.activity}</div>
      </div>

      <div className="min-w-0">
        <input className={INPUT} value={decision.service} onChange={(e) => onChange({ service: e.target.value })} />
        {src.missingService && <Badge variant="warning" className="mt-1">{t('tasks.serviceMissing')}</Badge>}
      </div>

      <select
        value={decision.typ}
        onChange={(e) => onChange({ typ: e.target.value as ScheduleTyp })}
        className={INPUT}
        disabled={!active}
      >
        {TYPS.map((ty) => (
          <option key={ty} value={ty}>{ty}</option>
        ))}
      </select>

      {decision.typ === 'Saisonal' ? (
        <div className="grid grid-cols-2 gap-1.5">
          <input className={INPUT} value={decision.von} onChange={(e) => onChange({ von: e.target.value })} placeholder="TT.MM." disabled={!active} />
          <input className={INPUT} value={decision.bis} onChange={(e) => onChange({ bis: e.target.value })} placeholder="TT.MM." disabled={!active} />
        </div>
      ) : (
        <span className="text-[12px] text-slate-400">—</span>
      )}

      <select
        value={decision.action}
        onChange={(e) => onChange({ action: e.target.value as 'create' | 'skip' })}
        className={INPUT}
      >
        <option value="create">{t('actions.create')}</option>
        <option value="skip">{t('actions.skip')}</option>
      </select>
    </div>
  );
}

export default TaskStep;
